/** @jsxImportSource @emotion/react */
import {
  ExclamationCircleOutlined,
  MoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { css } from "@emotion/react";
import { Button, Dropdown, Menu, message, Modal, Table } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import CreateService from "src/containers/aiPlatform/service/components/CreateService";
import EditService from "src/containers/aiPlatform/service/components/EditService";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { ServiceManage } from "src/models/aiPlatform";
import { AiServiceClient } from "src/services/AIPlatformService";

export default React.memo(function ServiceManagement() {
  const formatMessage = useFormatMessage();

  const [dataSource, setDataSource] = useState<ServiceManage[]>([]);
  const [isCreateShow, setIsCreateShow] = useState(false);
  const [isEditShow, setIsEditShow] = useState(false);
  const [detailData, setDetailData] = useState({} as ServiceManage);
  const [loading, setLoading] = useState(false);

  const list = useCallback(() => {
    setLoading(true);
    AiServiceClient.serviceList().then((res) => {
      setDataSource(res.data.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    list();
  }, [list]);

  const onEditShow = useCallback(() => {
    setIsEditShow(!isEditShow);
  }, [isEditShow]);

  const onEdit = useCallback(
    (record: ServiceManage) => {
      onEditShow();
      AiServiceClient.getServiceDetail(record?.serviceId).then((res) => {
        setDetailData(res.data.data);
      });
    },
    [onEditShow]
  );

  const onStatus = useCallback(
    (record: ServiceManage) => {
      AiServiceClient.changeStatus(record?.serviceId).then((res) => {
        if (res.data.success) {
          message.success(
            formatMessage(
              messageIds.aiManage.serviceManagement.changeStatusSuccessfully
            )
          );
        } else {
          message.error(
            formatMessage(
              messageIds.aiManage.serviceManagement.changeStatusFailed
            )
          );
        }
        list();
      });
    },
    [formatMessage, list]
  );

  const onDelete = useCallback(
    (record: ServiceManage) => {
      Modal.confirm({
        title: formatMessage(messageIds.common.tip),
        icon: <ExclamationCircleOutlined />,
        content: formatMessage(messageIds.installation.common.deleteInstation, {
          name: record?.platformServiceInfo?.name,
        }),
        okText: formatMessage(messageIds.common.confirm),
        cancelText: formatMessage(messageIds.common.cancel),
        onOk() {
          AiServiceClient.deleteService(record?.serviceId).then((res) => {
            if (res.data.success) {
              message.success(
                formatMessage(messageIds.common.deleteSuccessfully)
              );
            } else {
              message.error(formatMessage(messageIds.common.deleteFailed));
            }
            list();
          });
        },
      });
    },
    [formatMessage, list]
  );

  const menu = useCallback(
    (record: ServiceManage) => (
      <Menu>
        <Menu.Item
          key="update"
          css={css`
            font-size: 12px;
            :hover {
              color: rgb(51, 119, 255);
              border-radius: 4px;
              cursor: pointer;
              background: rgb(51, 119, 255, 0.1);
            }
          `}
          onClick={() => onEdit(record)}
        >
          {formatMessage(messageIds.common.edit)}
        </Menu.Item>
        <Menu.Item
          key="delete"
          css={css`
            font-size: 12px;
            :hover {
              color: rgb(51, 119, 255);
              border-radius: 4px;
              cursor: pointer;
              background: rgb(51, 119, 255, 0.1);
            }
          `}
          onClick={() => onDelete(record)}
        >
          {formatMessage(messageIds.common.delete)}
        </Menu.Item>
        <Menu.Item
          key="status"
          css={css`
            font-size: 12px;
            :hover {
              color: rgb(51, 119, 255);
              border-radius: 4px;
              cursor: pointer;
              background: rgb(51, 119, 255, 0.1);
            }
          `}
          onClick={() => onStatus(record)}
        >
          {record?.platformServiceInfo?.status === 1
            ? formatMessage(messageIds.aiManage.userManagment.disable)
            : formatMessage(messageIds.aiManage.userManagment.enable)}
        </Menu.Item>
      </Menu>
    ),
    [formatMessage, onDelete, onEdit, onStatus]
  );

  const columns = useMemo(
    () => [
      {
        title: formatMessage(messageIds.aiManage.serviceManagement.name),
        dataIndex: "name",
        render: (text: string, record: ServiceManage) => (
          <span>{record?.platformServiceInfo.name}</span>
        ),
      },
      {
        title: formatMessage(messageIds.aiManage.serviceManagement.aliasName),
        dataIndex: "aliasName",
        render: (text: string, record: ServiceManage) => (
          <span>{record?.platformServiceInfo.aliasName}</span>
        ),
      },
      {
        title: formatMessage(
          messageIds.aiManage.serviceManagement.aihubServiceName
        ),
        dataIndex: "aiHubServiceName",
        render: (text: string, record: ServiceManage) => (
          <span>{record?.platformServiceInfo.aiHubServiceName}</span>
        ),
      },
      {
        title: formatMessage(messageIds.aiManage.serviceManagement.version),
        dataIndex: "version",
        render: (text: string, record: ServiceManage) => (
          <span>{record?.platformServiceInfo.version}</span>
        ),
      },
      {
        title: formatMessage(
          messageIds.aiManage.serviceManagement.trialLimited
        ),
        dataIndex: "trialLimited",
        render: (text: string, record: ServiceManage) => (
          <span>{record?.platformServiceInfo.trialLimited}</span>
        ),
      },
      {
        title: formatMessage(messageIds.aiManage.serviceManagement.status),
        dataIndex: "status",
        render: (text: number, record: ServiceManage) =>
          record?.platformServiceInfo.status === 1 ? (
            <span
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <div
                css={css`
                  border-radius: 100%;
                  background-color: #00dd00;
                  width: 8px;
                  height: 8px;
                  margin-right: 7px;
                `}
              />
              {formatMessage(messageIds.aiManage.userManagment.enable)}
            </span>
          ) : (
            <span
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <div
                css={css`
                  border-radius: 100%;
                  background-color: red;
                  width: 8px;
                  height: 8px;
                  margin-right: 7px;
                `}
              />
              {formatMessage(messageIds.aiManage.userManagment.disable)}
            </span>
          ),
      },
      {
        title: formatMessage(messageIds.common.operation),
        dataIndex: "operation",
        render: (text: string, record: ServiceManage) => (
          <Dropdown
            overlay={menu(record)}
            placement="bottomLeft"
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              width: 30px;
              height: 30px;
              :hover {
                color: rgb(51, 119, 255);
                border-radius: 4px;
                cursor: pointer;
                background: rgb(51, 119, 255, 0.1);
              }
            `}
          >
            <MoreOutlined />
          </Dropdown>
        ),
      },
    ],
    [formatMessage, menu]
  );

  const onCreate = useCallback(() => {
    setIsCreateShow(!isCreateShow);
  }, [isCreateShow]);

  return (
    <div
      css={css`
        flex-grow: 1;
        padding: 30px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-right: 30px;
          padding-bottom: 30px;
          border-bottom: 1px solid rgb(202, 209, 224);
        `}
      >
        <div
          css={css`
            max-width: 1000px;
            margin-right: 20px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          <h1
            css={css`
              position: relative;
              margin: 0px 0px 8px;
              padding-left: 10px;
              line-height: 18px;
              font-size: 18px;
              font-weight: 600;
              color: rgb(61, 73, 102);
              ::before {
                content: "";
                position: absolute;
                left: 0px;
                width: 3px;
                height: 18px;
                background: rgb(51, 119, 255);
              }
            `}
          >
            {formatMessage(messageIds.menu.aiManagement.service.name)}
          </h1>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          css={css`
            padding: 4px 20px;
            border-radius: 4;
            background-color: rgb(51, 119, 255);
          `}
          onClick={onCreate}
        >
          {formatMessage(messageIds.common.create)}
        </Button>
      </div>

      <div
        css={css`
          margin-top: 30px;
        `}
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey={(record) => record?.serviceId}
          loading={loading}
        ></Table>
      </div>
      <CreateService
        isModalShow={isCreateShow}
        onShow={onCreate}
        list={list}
      ></CreateService>
      <EditService
        isModalShow={isEditShow}
        onShow={onEditShow}
        list={list}
        detailData={detailData}
      ></EditService>
    </div>
  );
});
